/**
 * A seção "Oportunidade automática" (13.21a2, FR-66 emendado, FR-68) — Configuração → Negócios.
 *
 * Desde a 13.21a1, todo número **novo** que conversa com alguém da equipe vira um Negócio sem
 * Empresa, no Funil e na Etapa da **regra daquela pessoa** — "do sdr é um funil e do vendedor é outro
 * funil" (Gustavo, 26/10/2026). Sem regra, nada nasce. Esta seção é onde a gestão escolhe a regra de
 * cada um.
 *
 * **Composta no SPA, de quatro leituras de três donos (AD-25)** — não há BFF:
 * - **as linhas** vêm de `GET /comunicacao/numeros`, só as com `tem_numero`: a regra de quem não tem
 *   número nunca dispara (sem número, não há mensagem). Para quem entra só por `gerir_funis_e_etapas`
 *   a resposta é a forma reduzida da a1, com `numero` e `situacao` nulos — e a seção não os mostra;
 * - **os nomes e o ativo** vêm de `GET /identidade/usuarios/responsaveis`: o desativado fica fora, e
 *   a ordem é alfabética;
 * - **as regras** vêm de `GET /negocios/regras-de-oportunidade`;
 * - **os Funis e as Etapas** vêm de `GET /negocios/funis`.
 *
 * **Cada linha grava sozinha, no `onChange`** (o molde do status da RAT): a primeira opção do Funil,
 * "Sem oportunidade automática", faz o `DELETE`; escolher a Etapa faz o `PUT`. Trocar o Funil limpa a
 * Etapa e **não grava nada** até haver Etapa nova — um Funil sem Etapa não é regra. Voltar ao Funil
 * gravado mostra de novo a Etapa gravada (a linha volta a ser o que o serviço tem), e voltar a "Sem
 * oportunidade automática" sem regra gravada só desfaz a escolha, sem ida à rede.
 *
 * **A recusa do serviço aparece verbatim na própria linha**, e os selects voltam ao valor gravado —
 * o rascunho some com ela. Enquanto uma linha grava, **só ela** fica travada (`aria-busy`): cada
 * linha tem as próprias mutações. As duas escritas invalidam as regras, e a linha destrava já
 * mostrando o que foi gravado.
 *
 * **Só aparece para quem tem `gerir_funis_e_etapas`** na sessão — e é conveniência: quem recusa é o
 * serviço (as três rotas da a1 são guardadas pela mesma permissão). Sem ela, o componente devolve
 * `null` **antes** de montar as consultas, e nenhuma vai à rede.
 *
 * **Não está no protótipo**: segue o padrão visual de "Previsão de fechamento" (a gramática de
 * `listas-de-dominio.css`). No celular, cada linha empilha o nome, o Funil e a Etapa — a virada é de
 * árvore, por `useEhCelular`, como no resto do shell.
 */

import { useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import '@/modulos/cadastro/listas-de-dominio.css'
import { useSituacaoDoWhatsapp } from '@/modulos/comunicacao/api'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { useEhCelular } from '@/shell/Shell'
import {
  type Funil,
  type RegraDeOportunidade,
  useApagarRegraDeOportunidade,
  useFunis,
  useGravarRegraDeOportunidade,
  useRegrasDeOportunidade,
} from './api'
import './oportunidade-automatica.css'

/** A permissão que abre a seção — a mesma que guarda as rotas da regra (AD-18). */
export const PERMISSAO_DA_OPORTUNIDADE_AUTOMATICA = 'gerir_funis_e_etapas'

export const TITULO_DA_OPORTUNIDADE_AUTOMATICA = 'Oportunidade automática'
export const APOIO_DA_OPORTUNIDADE_AUTOMATICA =
  'Todo número novo que conversar com esta pessoa vira um Negócio sem Empresa no Funil e na ' +
  'Etapa escolhidos.'
export const NINGUEM_COM_NUMERO = 'Ninguém da equipe tem número de WhatsApp ainda.'
export const SEM_OPORTUNIDADE_AUTOMATICA = 'Sem oportunidade automática'
export const ESCOLHA_A_ETAPA = 'Escolha a etapa'
/** O rascunho sem Etapa: nada foi gravado, e a linha diz isso. */
export const FALTA_A_ETAPA = 'Escolha a etapa para gravar a regra.'
export const CARREGANDO_A_OPORTUNIDADE = 'Carregando a oportunidade automática…'
/**
 * As marcas da regra órfã (revisão da 13.21): a pessoa tem regra gravada e saiu do filtro — foi
 * desativada, ou ficou sem número. A regra não dispara (sem número não há mensagem; inativa, o
 * consumidor não cria nada), mas continua gravada e prende a Etapa (FR-68): a linha aparece com a
 * marca, e "Sem oportunidade automática" a remove.
 */
export const MARCA_INATIVO = 'inativo'
export const MARCA_SEM_NUMERO = 'sem número'
export const USUARIO_SEM_NOME = 'Usuário sem nome'
export const TENTAR_DE_NOVO = 'Tentar de novo'

const ID_DO_TITULO = 'oportunidade-automatica-titulo'

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** Uma pessoa da equipe com número, e a regra dela (ou nenhuma). */
interface PessoaComNumero {
  usuarioId: string
  nome: string
  regra: RegraDeOportunidade | undefined
  /** A marca da regra órfã — `undefined` para quem está no filtro. */
  marca?: string
}

/** O que a linha mostra antes de o serviço confirmar: o Funil escolhido e a Etapa (ou nenhuma). */
interface Rascunho {
  funilId: string
  etapaId: string
}

function LinhaDaRegra({
  pessoa,
  funis,
  empilhada,
}: {
  pessoa: PessoaComNumero
  funis: readonly Funil[]
  empilhada: boolean
}) {
  const gravacao = useGravarRegraDeOportunidade()
  const apagamento = useApagarRegraDeOportunidade()
  const [rascunho, setRascunho] = useState<Rascunho | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const { usuarioId, nome, regra, marca } = pessoa
  const ocupada = gravacao.isPending || apagamento.isPending
  const funilId = rascunho?.funilId ?? regra?.funil_id ?? ''
  const etapaId = rascunho?.etapaId ?? regra?.etapa_id ?? ''
  const funil = funis.find((umFunil) => umFunil.id === funilId)
  const idDoFunil = `oportunidade-funil-${usuarioId}`
  const idDaEtapa = `oportunidade-etapa-${usuarioId}`

  /** A recusa: a mensagem do serviço na linha, e os selects de volta ao gravado. */
  function aoRecusar(falha: unknown) {
    setRascunho(null)
    setErro(mensagemDe(falha))
  }

  function escolherOFunil(novo: string) {
    setErro(null)
    if (novo === '') {
      if (regra === undefined) {
        // Sem regra gravada não há o que apagar: só desfaz a escolha.
        setRascunho(null)
        return
      }
      setRascunho({ funilId: '', etapaId: '' })
      apagamento.mutate(usuarioId, {
        onSuccess: () => {
          setRascunho(null)
        },
        onError: aoRecusar,
      })
      return
    }
    // O Funil gravado devolve a linha ao que o serviço tem; outro Funil limpa a Etapa e espera.
    setRascunho(novo === regra?.funil_id ? null : { funilId: novo, etapaId: '' })
  }

  function escolherAEtapa(nova: string) {
    if (nova === '' || funilId === '') return
    setErro(null)
    setRascunho({ funilId, etapaId: nova })
    gravacao.mutate(
      { usuarioId, regra: { funil_id: funilId, etapa_id: nova } },
      {
        onSuccess: () => {
          setRascunho(null)
        },
        onError: aoRecusar,
      },
    )
  }

  const faltaAEtapa =
    !ocupada && rascunho !== null && rascunho.funilId !== '' && rascunho.etapaId === ''

  return (
    <li className="listas__item" aria-label={nome} aria-busy={ocupada}>
      <div
        className={
          empilhada ? 'oportunidade__linha oportunidade__linha--empilhada' : 'oportunidade__linha'
        }
      >
        <span className="listas__valor oportunidade__nome">
          {nome}
          {marca === undefined ? null : (
            <span className="oportunidade__marca" data-marca-da-regra-orfa="">
              {marca}
            </span>
          )}
        </span>
        <div className="listas__campo oportunidade__campo">
          <label className="listas__rotulo" htmlFor={idDoFunil}>
            Funil
          </label>
          <select
            id={idDoFunil}
            aria-label={`Funil de ${nome}`}
            className="listas__entrada"
            value={funilId}
            disabled={ocupada}
            onChange={(evento) => {
              escolherOFunil(evento.target.value)
            }}
          >
            <option value="">{SEM_OPORTUNIDADE_AUTOMATICA}</option>
            {funis.map((umFunil) => (
              <option key={umFunil.id} value={umFunil.id}>
                {umFunil.nome}
              </option>
            ))}
          </select>
        </div>
        <div className="listas__campo oportunidade__campo">
          <label className="listas__rotulo" htmlFor={idDaEtapa}>
            Etapa
          </label>
          <select
            id={idDaEtapa}
            aria-label={`Etapa de ${nome}`}
            className="listas__entrada"
            value={funil === undefined ? '' : etapaId}
            disabled={ocupada || funil === undefined}
            onChange={(evento) => {
              escolherAEtapa(evento.target.value)
            }}
          >
            <option value="" disabled>
              {ESCOLHA_A_ETAPA}
            </option>
            {(funil?.etapas ?? []).map((umaEtapa) => (
              <option key={umaEtapa.id} value={umaEtapa.id}>
                {umaEtapa.nome}
              </option>
            ))}
          </select>
        </div>
      </div>
      {faltaAEtapa ? (
        <p className="listas__ajuda oportunidade__aviso" role="status">
          {FALTA_A_ETAPA}
        </p>
      ) : null}
      {erro === null ? null : (
        <p className="listas__erro oportunidade__aviso" role="alert">
          {erro}
        </p>
      )}
    </li>
  )
}

/** O cartão, montado só com a permissão: as quatro consultas nascem aqui, e não antes. */
function CartaoDaOportunidadeAutomatica() {
  const numeros = useSituacaoDoWhatsapp()
  const responsaveis = useResponsaveis()
  const regras = useRegrasDeOportunidade()
  const funis = useFunis()
  const empilhada = useEhCelular()

  const consultas = [numeros, responsaveis, regras, funis]
  const falha = consultas.find((consulta) => consulta.isError)

  function corpo() {
    if (falha !== undefined) {
      return (
        <p className="listas__erro" role="alert">
          {mensagemDe(falha.error)}{' '}
          <button
            type="button"
            className="listas__botao listas__botao--discreto"
            onClick={() => {
              for (const consulta of consultas) {
                if (consulta.isError) void consulta.refetch()
              }
            }}
          >
            {TENTAR_DE_NOVO}
          </button>
        </p>
      )
    }
    if (
      numeros.data === undefined ||
      responsaveis.data === undefined ||
      regras.data === undefined ||
      funis.data === undefined
    ) {
      return (
        <p className="listas__ajuda" role="status">
          {CARREGANDO_A_OPORTUNIDADE}
        </p>
      )
    }

    const ativos = new Map(
      responsaveis.data.filter((alguem) => alguem.ativo).map((alguem) => [alguem.id, alguem.nome]),
    )
    const regraDe = new Map(regras.data.map((umaRegra) => [umaRegra.usuario_id, umaRegra]))
    const pessoas: PessoaComNumero[] = numeros.data
      .filter((linha) => linha.tem_numero && ativos.has(linha.usuario_id))
      .map((linha) => ({
        usuarioId: linha.usuario_id,
        nome: ativos.get(linha.usuario_id) ?? '',
        regra: regraDe.get(linha.usuario_id),
      }))
      .sort((uma, outra) => uma.nome.localeCompare(outra.nome, 'pt-BR'))
    /*
     * As regras órfãs (revisão da 13.21): quem tem regra e está fora do filtro — inativo, ou sem
     * número. Elas vêm depois das do filtro, também em ordem alfabética, com a marca.
     */
    const noFiltro = new Set(pessoas.map((pessoa) => pessoa.usuarioId))
    const todos = new Map(responsaveis.data.map((alguem) => [alguem.id, alguem]))
    const orfas: PessoaComNumero[] = regras.data
      .filter((umaRegra) => !noFiltro.has(umaRegra.usuario_id))
      .map((umaRegra) => {
        const alguem = todos.get(umaRegra.usuario_id)
        return {
          usuarioId: umaRegra.usuario_id,
          nome: alguem?.nome ?? USUARIO_SEM_NOME,
          regra: umaRegra,
          marca: alguem !== undefined && alguem.ativo ? MARCA_SEM_NUMERO : MARCA_INATIVO,
        }
      })
      .sort((uma, outra) => uma.nome.localeCompare(outra.nome, 'pt-BR'))

    if (pessoas.length === 0 && orfas.length === 0) {
      return <p className="listas__ajuda">{NINGUEM_COM_NUMERO}</p>
    }
    return (
      <ul className="listas__lista" aria-label="Oportunidade automática por pessoa">
        {[...pessoas, ...orfas].map((pessoa) => (
          <LinhaDaRegra
            key={pessoa.usuarioId}
            pessoa={pessoa}
            funis={funis.data}
            empilhada={empilhada}
          />
        ))}
      </ul>
    )
  }

  return (
    <section className="listas" aria-labelledby={ID_DO_TITULO}>
      <div className="listas__cartao">
        <div className="listas__cabecalho">
          <h2 className="listas__titulo" id={ID_DO_TITULO}>
            {TITULO_DA_OPORTUNIDADE_AUTOMATICA}
          </h2>
          <span className="listas__ajuda">{APOIO_DA_OPORTUNIDADE_AUTOMATICA}</span>
        </div>
        <div className="listas__corpo">{corpo()}</div>
      </div>
    </section>
  )
}

export function OportunidadeAutomatica() {
  const { data: sessao } = useSessao()
  if (sessao?.permissoes[PERMISSAO_DA_OPORTUNIDADE_AUTOMATICA] !== 'concedida') {
    return null
  }
  return <CartaoDaOportunidadeAutomatica />
}
